import React, { useCallback, useEffect, useRef } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useDialogPlus } from "@/componentsUI/Dialog/dialogPlus";
import {
  DIALOG_VISIBILITY_CHANGED,
  isDialogPlusVisible,
} from "@/componentsUI/Dialog/dialogVisibility";
import DialogInviteWheel from "@/features/invite/dialogInviteWheel/DialogInviteWheel";
import useDialog from "@/utils/useDialog";
import { completeInviteWheelEntry } from "./inviteWheelEntry";

const ENTRY_KEY = "DialogInviteWheel";

const InviteWheelEntryContent = ({
  signal,
  onUnavailable,
}: {
  signal: AbortSignal;
  onUnavailable: () => void;
}) => {
  const { closeSelf } = useDialogPlus();
  useEffect(() => {
    const close = () => closeSelf(ENTRY_KEY);
    if (signal.aborted) close();
    else signal.addEventListener("abort", close, { once: true });
    return () => signal.removeEventListener("abort", close);
  }, [closeSelf, signal]);

  const handleUnavailable = useCallback(() => {
    closeSelf(ENTRY_KEY);
    onUnavailable();
  }, [closeSelf, onUnavailable]);
  return <DialogInviteWheel onUnavailable={handleUnavailable} />;
};

/** 复用 DialogPlus 转盘，以实际关闭/失败结束队列任务。 */
const InviteWheelEntryModal = NiceModal.create(
  ({ signal }: { signal: AbortSignal }) => {
    const { resolve, reject, remove } = useModal();
    const { push } = useDialog();
    const openedRef = useRef(false);
    const finishedRef = useRef(false);
    const finish = useCallback(() => {
      if (signal.aborted || finishedRef.current) return;
      finishedRef.current = true;
      completeInviteWheelEntry();
      resolve();
      remove();
    }, [remove, resolve, signal]);

    useEffect(() => {
      const onVisibilityChanged = () => {
        if (openedRef.current && !isDialogPlusVisible()) finish();
      };
      window.addEventListener(DIALOG_VISIBILITY_CHANGED, onVisibilityChanged);
      if (!openedRef.current && !signal.aborted) {
        openedRef.current = true;
        try {
          push({
            key: ENTRY_KEY,
            children: (
              <InviteWheelEntryContent signal={signal} onUnavailable={finish} />
            ),
          });
          // DialogPlus 尚未挂载时 push 不会呈现页面，不能留下悬挂任务。
          if (!isDialogPlusVisible()) finish();
        } catch (error) {
          reject(error);
          remove();
        }
      }
      return () =>
        window.removeEventListener(
          DIALOG_VISIBILITY_CHANGED,
          onVisibilityChanged
        );
    }, [finish, push, reject, remove, signal]);
    return null;
  }
);

export default InviteWheelEntryModal;
